import { coveredAt } from '../render/roadMapBridge';
import { micWords } from './stageBridge';

// sceneShots — where the camera stands for a scene, shot by shot.
//
// The website's Signal & Soul 3D stage (web components/stage/shotDirector) is
// the model: every shot is a camera on an arc around a target, chosen by the
// speaker's turn — a planned establishing view, then close-ups and
// over-the-shoulders on whoever is speaking, a two-shot to breathe. That stage
// has fixed marks; here the "marks" are wherever the artists are standing in
// the city, so a shot is computed from their positions instead of read from a
// set file.
//
// ⚠ THIS WORLD IS NOT IN METRES: a person is ~0.75 units tall, eyes at ~0.69.
//
// THE LINE OF ACTION. All of a scene's shots stay on one side of the line
// between the two people (`side`), chosen once, so nobody flips screen-side on
// a cut — the 180° rule.
//
// WHERE THE CAMERA MAY STAND: on open ground (the road map's covered texels are
// buildings), with nothing solid between it and who it is looking at. A shot
// tries its angles in order of preference first; only then comes in closer —
// and comes DOWN as it comes in, keeping its angle. (It used to slide straight
// in at the same height, so a high establishing view whose spot fell inside a
// shop ended up over the gap between the two, looking down at the pavement —
// Khari, 2026-10-23: "the camera is just looking at the sidewalk between them".)
//
// EVERY SHOT MOVES (Khari, 2026-10-24: "add more camera movement. While
// conversations are happening, pan and move instead of just focusing on a face
// the whole time. The face is fine, the still camera gets boring."). A shot is a
// MOVE, planned once when it starts (`planShot`) and played out by `shotAt`: the
// camera travels an arc around what it looks at — swinging round, pushing in,
// settling — and a pan carries the look from the listener to the speaker. The
// WHOLE path is checked against the street when it is planned, never frame by
// frame, so a move cannot flip to another angle halfway; a path that would run
// into a wall swings less, then holds still, then comes in closer (as above).
// A little handheld breath on top keeps even the slowest move alive.
//
// NOT IN ANYONE'S HEAD, NOT AT A WALL (Khari, 2026-10-30: "sometimes, the scene
// camera is too close to the artist and cuts into their head. In this same
// scene (happening at the listening room), one of the camera angles is pointing
// directly at a wall, no artists in the view."). Both at a NARROW PAVEMENT: a
// pair stands single file on it, 0.6 apart, a wall a quarter unit behind them.
//   · INTO A HEAD: the close-up stood 0.8–0.95 in front of the speaker — and
//     the listener stands 0.6 in front of them, so the camera landed IN the
//     listener's head (0.19 away; the near plane is 0.3, so the face was cut
//     open and the inside of the mouth showed). Walls were checked, people never.
//   · AT A WALL: when no angle passed, the last resort was an UNCHECKED spot —
//     inside the building, looking at its wall. And when the SECOND of the pair
//     spoke, every shot was planned from the far side of the line (the side was
//     read off speaker → listener, which flips with the speaker — the director
//     now hands it over the right way round), so at a pavement: the building
//     side, all walls, straight to that last resort.
// So a camera spot now also has to: keep NEAR_SAFE from EVERY body in the scene,
// subject or not; stand WALL_SAFE clear of a building (a road-map texel is 0.23
// units, so "on an open texel" can still be inside a wall's face); and SEE the
// people it is for — in frame, nothing solid in between, not behind someone
// else's head. One that cannot is pushed back along its line, then tries every
// angle round (this side of the line first); never an unchecked spot.

const FOV = { establish: 50, two: 40, ots: 34, close: 30, medium: 36, orbit: 44, pan: 30, drift: 34 };
const MOVE_MS = 8000;          // a move's length (a shot that lasts longer holds its last frame, breathing)
const ESTABLISH_MOVE_MS = 4500;
const PAN_MS = 1500;           // a pan from listener to speaker
const NEAR_SAFE = 0.55;        // camera ↔ anyone's body: the near plane (0.3) + a head (~0.08) + the nose and the handheld
const WALL_SAFE = 0.3;         // camera ↔ a building (a texel and a bit: the wall's true face can sit inside an "open" texel)
const HEAD_R = 0.13;           // a sight line this near someone else's head is a shot of the back of it
const MAX_FOV = 75;            // the widest a shot may open to keep its framing from nearer than planned

const covered = (x, z) => coveredAt(x, z, true);

// A 3D SIGHT TEST (2026-11-01). The road map knows buildings, not lamp posts,
// signs or trees: the first real still of a moment (render job 10) had a lamp
// post dead centre between the two. A moment's still is planned once, so it can
// afford real rays against the scene — city/momentSight sets this while it plans
// (and clears it after); `fits` then refuses any spot that cannot see each
// person's eyes and chest, and the usual search moves on to the next angle.
// Null for every live scene: nothing about them changes.
let sightTest = null;
export function setSightTest(fn) { sightTest = fn || null; }
const NONE = [];
const NO_CTX = { people: NONE, see: NONE, tanH: Infinity };
const RING8 = [[1, 0], [0.7071, 0.7071], [0, 1], [-0.7071, 0.7071], [-1, 0], [-0.7071, -0.7071], [0, -1], [0.7071, -0.7071]];

/** Nothing solid between the camera and (x, z) (the last `skip` units are the subject's own ground). */
function sightClearTo(pos, x, z, skip = 0.5) {
  const dx = x - pos[0]; const dz = z - pos[2];
  const L = Math.hypot(dx, dz);
  // (Steps finer than a texel: at 0.25 a one-texel wall crossed corner to corner could be stepped over.)
  for (let d = 0.2; d < L - skip; d += 0.15) {
    if (covered(pos[0] + (dx * d) / L, pos[2] + (dz * d) / L)) return false;
  }
  return true;
}

/** Open ground with WALL_SAFE of it all round. */
function groundClear(x, z) {
  if (covered(x, z)) return false;
  for (let i = 0; i < 8; i++) if (covered(x + RING8[i][0] * WALL_SAFE, z + RING8[i][1] * WALL_SAFE)) return false;
  return true;
}

// A person as the camera must keep clear of them: a body standing from the feet
// to the top of the head. `head` (the director's: 0.92 × height) is about the
// CHIN — eyes are at 0.92–0.97 × height, and a body drawn taller than its roster
// height higher still (Sassy La's at 1.02) — so the top is allowed well above it.
const topOf = (p) => p.top ?? p.head + 0.16;
const footOf = (p) => p.foot ?? p.head - 0.7;
const eyeOf = (p) => p.head + 0.03;
function bodyDist(c, p) {
  const t = topOf(p); const f = footOf(p);
  const dy = c[1] > t ? c[1] - t : c[1] < f ? f - c[1] : 0;
  return Math.hypot(c[0] - p.x, c[2] - p.z, dy);
}

/** `p` is across the frame of a camera at `c` looking at `look` (tanH: the half-width per unit of depth). */
function inFrame(c, look, p, tanH) {
  let fx = look[0] - c[0]; let fz = look[2] - c[2];
  const fl = Math.hypot(fx, fz) || 1; fx /= fl; fz /= fl;
  const vx = p.x - c[0]; const vz = p.z - c[2];
  const depth = vx * fx + vz * fz;
  return depth > 0.3 && Math.abs(vx * fz - vz * fx) <= depth * tanH * 0.9;
}

/** Someone else's head is in the way of `p`'s face. */
function hidden(c, p, people) {
  const ex = p.x - c[0]; const ey = eyeOf(p) - c[1]; const ez = p.z - c[2];
  const L2 = ex * ex + ey * ey + ez * ez || 1;
  for (let i = 0; i < people.length; i++) {
    const q = people[i];
    if (q === p) continue;
    const qx = q.x - c[0]; const qy = eyeOf(q) - c[1]; const qz = q.z - c[2];
    const t = (qx * ex + qy * ey + qz * ez) / L2;
    if (t <= 0 || t >= 1) continue;
    if (Math.hypot(ex * t - qx, ey * t - qy, ez * t - qz) < HEAD_R) return true;
  }
  return false;
}

/**
 * May the camera stand at `c` for these looks? Open ground clear of walls,
 * clear of every body in the scene (ctx.people), a clear line to each look and
 * — for each look — the people it is for (ctx.see[i]) in frame and not hidden.
 */
function fits(c, looks, ctx) {
  if (covered(c[0], c[2])) return false;
  const P = ctx.people;
  for (let i = 0; i < P.length; i++) if (bodyDist(c, P[i]) < NEAR_SAFE) return false;
  if (!groundClear(c[0], c[2])) return false;
  for (let j = 0; j < looks.length; j++) {
    const lk = looks[j];
    if (!sightClearTo(c, lk[0], lk[2])) return false;
    const see = ctx.see[j] || NONE;
    for (let i = 0; i < see.length; i++) {
      const p = see[i];
      if (!inFrame(c, lk, p, ctx.tanH) || hidden(c, p, P) || !sightClearTo(c, p.x, p.z)) return false;
      if (sightTest && !sightTest(c, p)) return false;   // last: the dearest check
    }
  }
  return true;
}

/** Who a shot is of, for `fits`: everyone in the scene to keep clear of, and who each look must show. */
function shotCtx(people, see, aspect) {
  return { people, see, tanH: Math.tan((MAX_FOV * Math.PI) / 360) * Math.max(0.3, aspect || 16 / 9) };
}

/** The lens that keeps a shot's framing from `k` × its planned distance (wider nearer, longer further). */
function framed(fov, k) {
  if (!k || Math.abs(k - 1) < 1e-3) return fov;
  return Math.min(MAX_FOV, (2 * Math.atan(Math.tan((fov * Math.PI) / 360) / k) * 180) / Math.PI);
}

/**
 * How far back to stand so a group `halfW` wide (each side of centre) fits
 * across a screen of this aspect at this vertical FOV — and, past `maxR`, the
 * wider FOV that makes it fit from there. A phone held upright sees a third of
 * what a desktop does across: a two-shot framed for 16:9 showed the gap between
 * two people and neither of them.
 */
function fitAcross(r, halfW, fov, aspect, maxR) {
  const tanH = Math.tan((fov * Math.PI) / 360) * Math.max(0.3, aspect || 16 / 9);
  let R = Math.max(r, halfW / tanH);
  let F = fov;
  if (R > maxR) {
    R = maxR;
    F = Math.min(75, (2 * Math.atan(halfW / (R * Math.max(0.3, aspect || 16 / 9))) * 180) / Math.PI);
  }
  return { r: R, fov: F };
}

/** Which side of the line A→B has more open ground: +1 or -1. */
export function chooseSide(A, B) {
  const dx = B.x - A.x; const dz = B.z - A.z; const L = Math.hypot(dx, dz) || 1;
  const nx = -dz / L; const nz = dx / L;
  const mx = (A.x + B.x) / 2; const mz = (A.z + B.z) / 2;
  let open = 0;
  // (1 unit out too, 2026-10-30: at a narrow pavement the wall is a quarter unit
  // behind them, and a shallow building with open ground behind it could tie.)
  for (const r of [1, 2, 3.5, 5]) open += (covered(mx + nx * r, mz + nz * r) ? 0 : 1) - (covered(mx - nx * r, mz - nz * r) ? 0 : 1);
  return open >= 0 ? 1 : -1;
}

// ── Moves ────────────────────────────────────────────────────────────────────

const lerp = (a, b, k) => a + (b - a) * k;
const easeOut = (u) => 1 - (1 - u) * (1 - u);          // moving from the first frame, settling at the end
const smooth = (u) => u * u * (3 - 2 * u);
const world = (a) => [Math.sin(a), Math.cos(a)];        // rotationY convention: 0 = +z

/**
 * An arc around a pivot. `dir(a)` turns an angle into a ground direction (the
 * scene's own frame or the world's); `r` and `y` run from their first value to
 * their second over the move. Returns mk(a0, da, k) → the camera at e (0..1):
 * starting at angle a0, swinging by da, and brought in toward the look by k
 * (lowered with it, so the angle holds).
 */
function arc({ pivot, dir, r: [r0, r1], y: [y0, y1], lookY }) {
  return (a0, da, k = 1) => (e) => {
    const [dx, dz] = dir(a0 + da * e);
    const r = lerp(r0, r1, e) * k;
    const y = lookY + (lerp(y0, y1, e) - lookY) * k;
    return [pivot[0] + dx * r, y, pivot[1] + dz * r];
  };
}

const MOVE_E = [0, 0.2, 0.4, 0.6, 0.8, 1];   // where a moving path is checked (fine enough that a swing cannot pass through a head between two)
const STILL_E = [0, 0.5, 1];
const COME_IN = [0.8, 0.65, 0.5, 0.4];
const PUSH_BACK = [1.3, 1.65];
const RING_K = [1, 1.3, 0.8, 1.65];
const RING_STEPS = 12;                        // every 15° round, nearest the first choice first
const LAST_K = [1, 1.3, 1.65, 2.2, 3];
const ALONG_LINE = [Math.PI / 2, -Math.PI / 2];

/**
 * The first move whose whole path is usable (`fits`), in order of preference:
 * each start angle with its full swing, then half the swing, then held still;
 * then each brought in closer (down to `minK` of the distance), still; then
 * pushed back along its line (2026-10-30: too near a head is cured by stepping
 * BACK, which coming in never could); then, with `ring`, every angle round,
 * nearest the first choice first. `clear` is set only on a move whose whole
 * path was found usable, `k` on every move (its distance as a share of the
 * planned one — `framed` keeps the framing). Never fails, and never returns a
 * spot inside anyone: the last resort is pushed back until it is clear of every
 * body (and of the buildings, wherever that can be found).
 */
function pickMove(mk, starts, swing, looks, minK = 0.45, ctx = NO_CTX, ring = true) {
  const swingOf = (i) => (Array.isArray(swing) ? swing[i] : swing);   // one for all, or one per start
  const ok = (at, moving) => (moving ? MOVE_E : STILL_E).every((e) => fits(at(e), looks, ctx));
  const found = (at, k) => Object.assign(at, { clear: true, k });
  for (const scale of [1, 0.5, 0]) {
    for (let i = 0; i < starts.length; i++) { const da = swingOf(i) * scale; const at = mk(starts[i], da, 1); if (ok(at, da !== 0)) return found(at, 1); }
  }
  for (const k of COME_IN) {
    if (k < minK - 1e-6) continue;
    for (const a0 of starts) { const at = mk(a0, 0, k); if (ok(at, false)) return found(at, k); }
  }
  for (const k of PUSH_BACK) {
    for (const a0 of starts) { const at = mk(a0, 0, k); if (ok(at, false)) return found(at, k); }
  }
  const round = [];
  for (let j = 1; j <= RING_STEPS; j++) { round.push(starts[0] + (j * Math.PI) / RING_STEPS); if (j < RING_STEPS) round.push(starts[0] - (j * Math.PI) / RING_STEPS); }
  // Straight along the line (ctx.along): down a narrow pavement or an alley the
  // open ground is a strip a few degrees wide, which 15° steps step over.
  const along = ctx.along || NONE;
  if (ring) {
    for (const k of RING_K) {
      if (k < minK - 1e-6) continue;
      for (const a0 of along) { const at = mk(a0, 0, k); if (ok(at, false)) return found(at, k); }
      for (const a0 of round) { const at = mk(a0, 0, k); if (ok(at, false)) return found(at, k); }
    }
  }
  // Nothing passes. Clear of every body, always; then, wherever it can be had,
  // out of the buildings with a clear line to what it looks at; then out of them.
  const clearOfPeople = (at) => STILL_E.every((e) => { const c = at(e); return ctx.people.every((p) => bodyDist(c, p) >= NEAR_SAFE); });
  const outside = (at) => STILL_E.every((e) => { const c = at(e); return groundClear(c[0], c[2]); });
  const sees = (at) => STILL_E.every((e) => { const c = at(e); return sightClearTo(c, looks[0][0], looks[0][2]); });
  let open = null; let safe = null;
  for (const k of LAST_K) {
    for (const a0 of [...starts, ...along, ...round]) {
      const at = mk(a0, 0, k);
      if (!clearOfPeople(at)) continue;
      if (!safe) safe = Object.assign(at, { k });
      if (!outside(at)) continue;
      if (sees(at)) return Object.assign(at, { k });
      if (!open) open = Object.assign(at, { k });
    }
  }
  return open || safe || Object.assign(mk(starts[0], 0, LAST_K[LAST_K.length - 1]), { k: LAST_K[LAST_K.length - 1] });
}

/**
 * One shot, planned: where the camera goes over the shot, and what it looks at.
 * @param kind    'establish' | 'two' | 'ots' | 'close' | 'pan' | 'orbit' (a pair) ·
 *                'establish' | 'close' | 'medium' | 'orbit' | 'drift' (alone)
 * @param S       the speaker (or the one person) { x, z, head, facing }
 * @param L       the listener, or null for someone alone
 * @param side    +1 / -1, the scene's side of the line of action, read from S → L
 *                (a director whose speaker changes flips it with them: the line is the same)
 * @param aspect  the screen's (a phone held upright frames wider)
 * @param turn    +1 / -1, which way this shot swings (varies shot to shot)
 * @returns { at(e) → [x, y, z], look0, look1, pan, fov, dur }
 */
export function planShot(kind, S, L, side, aspect = 16 / 9, turn = 1) {
  // Everyone in the scene, to keep clear of whoever the shot is of (2026-10-30).
  const people = L ? [S, L] : [S];
  const ctx = (...see) => shotCtx(people, see, aspect);
  if (!L) {
    // Alone: in front of them, by their facing (rotationY: 0 = +z).
    const f = S.facing || 0;
    const me = ctx([S]);
    if (kind === 'orbit' || kind === 'establish') {
      const look = [S.x, S.head * 0.8, S.z];
      const mk = arc({ pivot: [S.x, S.z], dir: world, r: [3.6, 3.2], y: [1.35, 1.15], lookY: look[1] });
      const starts = [0, 0.5, -0.5, 1.0, -1.0, Math.PI].map((da) => f + side * 0.6 + da);
      const at = pickMove(mk, starts, side * 0.65, [look], 0.4, me);
      return { at, look0: look, look1: look, fov: framed(FOV.orbit, at.k), dur: kind === 'establish' ? ESTABLISH_MOVE_MS : MOVE_MS };
    }
    const look = [S.x, S.head * 0.97, S.z];
    if (kind === 'drift') {
      // A slow walk round them, rising a little: the street turning behind them.
      const mk = arc({ pivot: [S.x, S.z], dir: world, r: [1.35, 1.25], y: [S.head * 0.92, S.head * 1.06], lookY: look[1] });
      const starts = [0.9, -0.9, 0.5, -0.5].map((k) => f + side * k);
      const at = pickMove(mk, starts, -side * turn * 0.75, [look], 0.45, me);
      return { at, look0: look, look1: look, fov: framed(FOV.drift, at.k), dur: MOVE_MS };
    }
    const medium = kind === 'medium';
    const mk = arc({ pivot: [S.x, S.z], dir: world, r: medium ? [1.85, 1.55] : [0.95, 0.8], y: medium ? [S.head * 1.06, S.head * 0.99] : [S.head, S.head * 0.99], lookY: look[1] });
    // Front, then three-quarters, then profile — this scene's side first —
    // each swinging round across their face (which way: the shot's `turn`).
    const ks = [0.25, 0.6, 1.1, -0.25, -0.6, -1.1];
    const swing = ks.map((k) => -side * Math.sign(k) * (medium ? 0.35 : 0.3) * turn);
    const at = pickMove(mk, ks.map((k) => f + side * k), swing, [look], 0.45, me);
    return { at, look0: look, look1: look, fov: framed(medium ? FOV.medium : FOV.close, at.k), dur: MOVE_MS };
  }

  const dx = L.x - S.x; const dz = L.z - S.z; const D = Math.hypot(dx, dz) || 0.6;
  const ux = dx / D; const uz = dz / D;                 // speaker → listener
  const nx = -uz * side; const nz = ux * side;          // out to the camera's side
  const mx = (S.x + L.x) / 2; const mz = (S.z + L.z) / 2;
  const headMid = (S.head + L.head) / 2;
  const halfW = D / 2 + 0.35;   // both of them, and a little air
  // Around the middle of the two, in the scene's own frame: angle 0 is straight
  // out to side `sd`, and it turns toward the speaker's end as it grows. This
  // side of the line first (the 180° rule), the other only if this one is all walls.
  const sceneDir = (sd) => (a) => {
    const bx = -uz * sd; const bz = ux * sd;
    return [bx * Math.cos(a) - ux * Math.sin(a), bz * Math.cos(a) - uz * Math.sin(a)];
  };
  const around = (rr, yy, look, offsets, swing, looks, minK, c) => {
    c.along = ALONG_LINE;   // ±90° in the scene's frame: down the pavement, either way
    for (const sd of [side, -side]) {
      const mk = arc({ pivot: [mx, mz], dir: sceneDir(sd), r: rr, y: yy, lookY: look[1] });
      const at = pickMove(mk, offsets, swing, looks, 1, c, false);   // this side, no coming in yet
      if (at.clear) return at;
    }
    const mk = arc({ pivot: [mx, mz], dir: sceneDir(side), r: rr, y: yy, lookY: look[1] });
    return pickMove(mk, offsets, swing, looks, minK, c);
  };

  if (kind === 'establish') {
    // A planned view of the place: wide, high, arcing in.
    const fit = fitAcross(5.6, halfW * 1.6, FOV.establish, aspect, 8);
    const look = [mx, headMid * 0.85, mz];
    const at = around([fit.r, fit.r * 0.86], [2.0, 1.6], look, [-0.35, 0.1, -0.8, 0.55, -1.25], 0.45, [look], 0.4, ctx([S, L]));
    return { at, look0: look, look1: look, fov: framed(fit.fov, at.k), dur: ESTABLISH_MOVE_MS };
  }
  if (kind === 'two' || kind === 'orbit') {
    // Both of them. The two-shot drifts round and in; the orbit circles them.
    const orbit = kind === 'orbit';
    const fit = fitAcross(orbit ? 2.2 : 2.5, halfW, FOV.two, aspect, orbit ? 4 : 5);
    const look = [mx, headMid * (orbit ? 0.9 : 0.93), mz];
    const offsets = orbit ? [-0.5 * turn, 0, 0.5 * turn, -0.9 * turn] : [0, 0.35, -0.35, 0.7, -0.7];
    const at = around([fit.r, fit.r * (orbit ? 0.92 : 0.86)], [headMid * (orbit ? 1.18 : 1.02), headMid * (orbit ? 1.0 : 0.97)], look, offsets, (orbit ? 0.95 : 0.35) * turn, [look], 0.6, ctx([S, L]));
    return { at, look0: look, look1: look, fov: framed(fit.fov, at.k), dur: MOVE_MS };
  }
  if (kind === 'pan') {
    // From the one who just spoke to the one speaking now: close enough that
    // the look has to travel, on our side of them.
    const lookL = [L.x, L.head * 0.97, L.z]; const lookS = [S.x, S.head * 0.97, S.z];
    const r = Math.max(1.15, D * 1.25);
    const at = around([r, r * 0.92], [headMid * 1.0, headMid * 0.97], lookS, [0.15, -0.15, 0.45, -0.45], 0.12 * turn, [lookL, lookS], 0.6, ctx([L], [S]));
    return { at, look0: lookL, look1: lookS, pan: PAN_MS, fov: framed(FOV.pan, at.k), dur: MOVE_MS };
  }
  if (kind === 'ots') {
    // Over the listener's shoulder, onto the speaker's face: far enough back and
    // wide enough of the shoulder that the speaker is in the frame, not the
    // listener's back (the first version filled the frame with a jacket). The
    // move: pushing in, sliding a little off the line so the shoulder clears.
    const look = [S.x, S.head * 0.97, S.z];
    const back = 1.15;
    const y = L.head * 1.08;
    const towardL = Math.atan2(L.x - S.x, L.z - S.z);
    const starts = [0.7, 0.85, -0.7].map((w) => {
      const sx = w > 0 ? nx : -nx; const sz = w > 0 ? nz : -nz;
      return Math.atan2(L.x + ux * back + sx * Math.abs(w) - S.x, L.z + uz * back + sz * Math.abs(w) - S.z);
    });
    const r = Math.hypot(D + back, 0.75);
    const mk = arc({ pivot: [S.x, S.z], dir: world, r: [r, r * 0.9], y: [y, L.head * 1.04], lookY: look[1] });
    let off = starts[0] - towardL; off = Math.atan2(Math.sin(off), Math.cos(off));
    const at = pickMove(mk, starts, Math.sign(off || 1) * 0.1, [look], 0.45, ctx([S]));
    return { at, look0: look, look1: look, fov: framed(FOV.ots, at.k), dur: MOVE_MS };
  }
  // close: in front of the speaker, a little off the line toward our side,
  // swinging slowly round their face. (In front of them is where the LISTENER
  // stands: at a pavement's 0.6 apart the first angles put the camera in their
  // head — `fits` now refuses those, and the last start, three-quarters to
  // profile from our side, is the one a narrow pavement leaves.)
  const look = [S.x, S.head * 0.98, S.z];
  const starts = [[0.82, 0.58], [0.6, 0.8], [0.95, 0.3], [0.82, -0.58], [0.4, 0.92]].map(([a, b]) => Math.atan2(ux * a + nx * b, uz * a + nz * b));
  const mk = arc({ pivot: [S.x, S.z], dir: world, r: [0.95, 0.8], y: [S.head * 1.0, S.head * 0.98], lookY: look[1] });
  const at = pickMove(mk, starts, 0.3 * turn, [look], 0.45, ctx([S]));
  return { at, look0: look, look1: look, fov: framed(FOV.close, at.k), dur: MOVE_MS };
}

/**
 * The camera `ms` into a planned shot. `tSec` (the city clock, in seconds)
 * drives the handheld breath, so everyone watching sees the same frame.
 * @returns { pos: [x, y, z], look: [x, y, z], fov }
 */
export function shotAt(plan, ms, tSec = 0) {
  const e = easeOut(Math.min(1, Math.max(0, ms / plan.dur)));
  const p = plan.at(e);
  const k = plan.pan ? smooth(Math.min(1, Math.max(0, ms / plan.pan))) : 1;
  const look = [lerp(plan.look0[0], plan.look1[0], k), lerp(plan.look0[1], plan.look1[1], k), lerp(plan.look0[2], plan.look1[2], k)];
  // Handheld: a few centimetres, slower than a breath, smaller up close.
  const amp = Math.min(0.016, 0.005 + 0.004 * Math.hypot(p[0] - look[0], p[2] - look[2])) * (plan.shake ?? 1);
  const sx = Math.sin(tSec * 0.83) + 0.5 * Math.sin(tSec * 2.1 + 1.3);
  const sy = Math.sin(tSec * 1.07 + 2.0) + 0.4 * Math.sin(tSec * 2.7);
  const sz = Math.cos(tSec * 0.71 + 0.4) + 0.5 * Math.sin(tSec * 1.9 + 0.7);
  return {
    pos: [p[0] + sx * amp, p[1] + sy * amp * 0.6, p[2] + sz * amp],
    look: [look[0] + sx * amp * 0.35, look[1] + sy * amp * 0.25, look[2] + sz * amp * 0.35],
    fov: plan.fov,
  };
}

/**
 * The shot for a line, by its place in the scene. Deterministic, so a
 * recording and a live viewing cut the same way:
 *   the first moments of a scene  → establishing
 *   then by line                  → close · over-the-shoulder · pan · two-shot · orbit, …
 *   a long line                   → changes shot halfway
 */
export function shotKindFor(lineIdx, alone, half = false) {
  const pairCycle = ['close', 'ots', 'pan', 'two', 'close', 'orbit', 'ots', 'pan'];
  const soloCycle = ['close', 'medium', 'orbit', 'close', 'drift'];
  const cycle = alone ? soloCycle : pairCycle;
  return cycle[(Math.max(0, lineIdx) + (half ? 2 : 0)) % cycle.length];
}

// ── The plaza stage: scene view for a set ────────────────────────────────────
//
// Khari, 2026-10-28: *"When someone is watching a performance at the plaza,
// allow them to watch it in this mode for the duration of the performance,
// with a leave button."* A set has no lines to cut on, so the cuts follow the
// MUSIC instead: a wide of the whole stage as each song starts, then a shot
// every few seconds round the performer — from the crowd, on the stage's
// wings, from behind them looking out at the square — and a wide again between
// songs. Timed from each song's start on the city clock, so everyone watching
// sees the same shot at the same moment, whenever they stepped in.
//
// The stage's own frame: its front faces `facing` (rotationY; 0 = +z, out over
// the square). Heights are from `floor`, the ground the square stands on (the
// stage top is STAGE_Y above it, the performer's eyes ~0.69 above that).

const STAGE_SHOT_MS = 7000;    // a cut every 7 s (a move runs 8: every shot is still moving when it cuts)
const STAGE_WIDE_MS = 6500;    // the wide that opens each song
const STAGE_CYCLE = ['front', 'close', 'crowd', 'wing', 'sweep', 'close', 'reverse', 'front', 'wing', 'close', 'crowd', 'sweep'];
const STAGE_FOV = { wide: 46, front: 34, close: 30, crowd: 38, wing: 32, reverse: 46, sweep: 40 };
// The pit cameras' long lens, by framing (city/stageCuts picks one per cut).
// From 4.7 units away (a person is 0.75 tall): waist up · head and shoulders ·
// eyes to mouth. (12.5° there shows the whole body — sized from frame heights.)
const PIT_FOV = { medium: 6.5, close: 3.6, tight: 2.2 };

/**
 * Which shot of a set this instant is, and when it started (both on the city
 * clock, so a recording and every live viewer cut together).
 * @param show  { t0, t1, tracks: [{ at, durMs }] }
 * @param onStage  the performer is standing on the stage (else: wides only)
 * @returns { kind, key, start, turn, song }
 */
export function stageShotFor(show, t, onStage) {
  // ON THE MIC (opening, between songs, closing: API Stage.planSet): close on
  // them for as long as they speak. Music is off then, so this is the steady branch.
  const said = onStage ? micWords(show) : [];
  for (let k = 0; k < said.length; k++) {
    const [which, w] = said[k];
    const s0 = show.t0 + (w.at || 0);
    const s1 = s0 + (Number(w.dur) || 3) * 1000;
    if (t >= s0 - 600 && t < s1 + 400) return { kind: 'close', key: `words:${which}`, start: s0 - 600, turn: k % 2 ? -1 : 1, song: -1 };
  }
  const tracks = show.tracks || [];
  let i = -1;
  for (let k = 0; k < tracks.length; k++) if (t >= show.t0 + (tracks[k].at || 0)) i = k;
  const tr = tracks[i];
  const songStart = tr ? show.t0 + (tr.at || 0) : show.t0;
  const playing = tr && t < songStart + (tr.durMs || 0);
  // Before the first song, between songs, or nobody up there yet: the whole stage.
  if (!onStage || !playing) {
    // (From a fixed instant, never `t`: the move would restart every frame.)
    const from = !tr ? show.t0 - 60000 : playing ? songStart : songStart + (tr.durMs || 0);
    return { kind: 'wide', key: `wide:${i}:${playing ? 1 : 0}`, start: from, turn: i % 2 ? -1 : 1, song: i };
  }
  const into = t - songStart;
  if (into < STAGE_WIDE_MS) return { kind: 'wide', key: `${i}:wide`, start: songStart, turn: i % 2 ? -1 : 1, song: i };
  const k = Math.floor((into - STAGE_WIDE_MS) / STAGE_SHOT_MS);
  const kind = STAGE_CYCLE[(k + i * 3) % STAGE_CYCLE.length];
  return { kind, key: `${i}:${k}`, start: songStart + STAGE_WIDE_MS + k * STAGE_SHOT_MS, turn: (k + i) % 2 ? -1 : 1, song: i };
}

/**
 * One stage shot, planned (played out by `shotAt`, like every other shot).
 * @param kind   'wide' | 'jib' | 'front' | 'close' | 'pit' | 'crowd' | 'wing' | 'reverse' | 'sweep'
 * @param stage  { x, z, facing, floor }
 * @param P      the performer { x, z, head } — or null (nobody up yet: the stage itself)
 * @param side   +1 / -1, the set's favoured side
 * @param opts   { framing: 'medium' | 'close' | 'tight' } for 'pit'
 */
export function planStageShot(kind, stage, P, side = 1, aspect = 16 / 9, turn = 1, opts = {}) {
  const f = stage.facing || 0;
  const fl = stage.floor || 0;
  const fwd = world(f);
  const S = P || { x: stage.x, z: stage.z, head: fl + 1.0 };
  const face = [S.x, S.head - 0.03, S.z];
  const plan = (at, look, fov, dur = MOVE_MS) => ({ at, look0: look, look1: look, fov: framed(fov, at.k), dur });
  // Clear of the performer, and them in the frame (2026-10-30, as every scene shot).
  const who = shotCtx(P ? [S] : NONE, P ? [[S]] : NONE, aspect);

  if (kind === 'wide') {
    // From the back of the square, high, pushing in on the whole stage and its screen.
    const fit = fitAcross(12, 3.9, STAGE_FOV.wide, aspect, 19);
    const look = [stage.x + fwd[0] * 0.3, fl + 1.05, stage.z + fwd[1] * 0.3];
    const mk = arc({ pivot: [stage.x, stage.z], dir: world, r: [fit.r, fit.r * 0.88], y: [fl + 2.4, fl + 1.9], lookY: look[1] });
    const starts = [0, 0.22, -0.22, 0.4, -0.4].map((d) => f + d * turn);
    return plan(pickMove(mk, starts, 0.16 * turn, [look], 0.5, who), look, fit.fov, STAGE_WIDE_MS + 500);
  }
  if (kind === 'jib') {
    // THE JIB: a crane at the back of the square, rising and swinging across the
    // stage — the live show's wide, more travel than the opening one.
    const fit = fitAcross(11, 3.6, STAGE_FOV.wide, aspect, 18);
    const look = [stage.x + fwd[0] * 0.2, fl + 1.0, stage.z + fwd[1] * 0.2];
    const mk = arc({ pivot: [stage.x, stage.z], dir: world, r: [fit.r * 1.04, fit.r * 0.92], y: [fl + 3.0, fl + 2.1], lookY: look[1] });
    const starts = [-0.32, 0.32, -0.18, 0.18].map((d) => f + d * turn);
    return plan(pickMove(mk, starts, [0.5 * turn, -0.5 * turn, 0.3 * turn, -0.3 * turn], [look], 0.5, who), look, fit.fov);
  }
  if (kind === 'pit') {
    // THE PIT: a long lens from the front of the crowd, low, looking up — the
    // concert close-up. From far back on a narrow lens rather than a camera in
    // their face: that is what a live show's close-ups look like. Left or right
    // of centre by `turn`, drifting a little as an operator does.
    const framing = PIT_FOV[opts.framing] ? opts.framing : 'close';
    // `head` is ~ the chin (0.92 × height; eyes measured at 0.92–0.97, and a
    // body drawn taller than its roster height higher still — Sassy La 1.02):
    // medium and close aim above it, so neither loses the top of a head.
    const look = [S.x, S.head + (framing === 'medium' ? -0.1 : framing === 'tight' ? 0.025 : 0.03), S.z];
    const mk = arc({ pivot: [S.x, S.z], dir: world, r: [4.7, 4.55], y: [fl + 0.52, fl + 0.58], lookY: look[1] });
    const starts = [0.5, 0.35, 0.65].map((d) => f + d * turn);
    // A long lens magnifies every tremor: this operator is on a monopod.
    return { ...plan(pickMove(mk, starts, -0.06 * turn, [look], 0.45, who), look, PIT_FOV[framing]), shake: framing === 'medium' ? 0.3 : 0.15 };
  }
  if (kind === 'front') {
    // Straight on from the front row, at a watcher's eye height: looking up at them, the wall behind.
    const mk = arc({ pivot: [S.x, S.z], dir: world, r: [2.9, 2.4], y: [fl + 0.8, fl + 0.72], lookY: face[1] });
    const starts = [0.15, -0.15, 0.4, -0.4].map((d) => f + d * side);
    return plan(pickMove(mk, starts, -0.3 * turn, [face], 0.45, who), face, STAGE_FOV.front);
  }
  if (kind === 'close') {
    // Their face, from a touch below the eyes, swinging slowly across it. Head
    // and shoulders, with room above: a body drawn taller than its roster
    // height (Sassy La's eyes sit at 1.02× it; most at 0.92–0.97×) must not
    // lose the top of its head.
    const mk = arc({ pivot: [S.x, S.z], dir: world, r: [1.2, 1.0], y: [S.head - 0.05, S.head - 0.09], lookY: face[1] });
    const starts = [0.3, 0.6, -0.3, -0.6].map((d) => f + d * side);
    return plan(pickMove(mk, starts, 0.3 * turn, [face], 0.45, who), face, STAGE_FOV.close);
  }
  if (kind === 'crowd') {
    // Down among whoever is watching, low, rising a little: heads in the foreground, the stage over them.
    const look = [S.x, S.head - 0.15, S.z];
    const mk = arc({ pivot: [S.x, S.z], dir: world, r: [6.0, 5.2], y: [fl + 0.5, fl + 0.6], lookY: look[1] });
    const starts = [0.35, -0.35, 0.15, -0.15].map((d) => f + d * turn);
    return plan(pickMove(mk, starts, -0.12 * turn, [look], 0.45, who), look, STAGE_FOV.crowd);
  }
  if (kind === 'wing') {
    // From the side of the stage, up there with them: the square opening out behind.
    const mk = arc({ pivot: [S.x, S.z], dir: world, r: [2.3, 2.0], y: [S.head + 0.02, S.head - 0.02], lookY: face[1] });
    const starts = [1.2, -1.2].map((d) => f + d * turn);
    return plan(pickMove(mk, starts, [-0.25 * turn, 0.25 * turn], [face], 0.45, who), face, STAGE_FOV.wing);
  }
  if (kind === 'reverse') {
    // The view from the stage: just behind their shoulder, looking out over the square.
    const look = [S.x + fwd[0] * 7, fl + 0.55, S.z + fwd[1] * 7];
    const mk = arc({ pivot: [S.x, S.z], dir: world, r: [0.95, 0.85], y: [S.head + 0.12, S.head + 0.08], lookY: look[1] });
    // (Closer behind them on a phone held upright: its narrow frame lost them off the edge.)
    const off = aspect < 1 ? 0.14 : 0.35;
    const starts = [off, -off].map((d) => f + Math.PI + d * side);
    return plan(pickMove(mk, starts, -0.12 * side * (aspect < 1 ? 0.5 : 1), [look], 0.45, shotCtx(P ? [S] : NONE, NONE, aspect)), look, STAGE_FOV.reverse);
  }
  // sweep: a crane move across the front of the stage, coming down as it goes.
  const look = [S.x, S.head - 0.1, S.z];
  const mk = arc({ pivot: [S.x, S.z], dir: world, r: [4.6, 4.0], y: [fl + 1.9, fl + 1.35], lookY: look[1] });
  return plan(pickMove(mk, [f - 0.8 * turn, f + 0.8 * turn, f - 0.4 * turn], [1.6 * turn, -1.6 * turn, 0.8 * turn], [look], 0.45, who), look, STAGE_FOV.sweep);
}
